import React, { useEffect, useState } from 'react';
import { dataService, DoctorOption, ResolvedDoctor } from '../../services/dataService';

/**
 * What the user has chosen in the doctor picker (FR-02).
 *
 * The list is the doctor accounts the admin assigned to the chosen hospital.
 * 'unlisted' is the escape hatch for a treating doctor who has no account yet:
 * the name is typed and stored with no doctor_id, and the dashboard flags it
 * so the admin knows whose account to create. Without it a family at a
 * hospital the admin has not staffed yet could not finish the profile at all.
 */
export type DoctorSelection =
  | { kind: 'none' }
  | { kind: 'listed'; id: string; name: string }
  | { kind: 'unlisted'; name: string };

const UNLISTED_VALUE = '__unlisted__';

export const emptyDoctorSelection: DoctorSelection = { kind: 'none' };

/** Lifts a stored doctor off a profile row into the picker's shape. */
export function doctorSelectionFromProfile(profile: {
  doctor_id?: string | null;
  doctor_name?: string | null;
} | null): DoctorSelection {
  if (profile?.doctor_id) {
    return { kind: 'listed', id: profile.doctor_id, name: profile.doctor_name ?? '' };
  }
  if (profile?.doctor_name) {
    return { kind: 'unlisted', name: profile.doctor_name };
  }
  return emptyDoctorSelection;
}

/** Resolves a selection to the doctor columns to store on the profile. */
export function resolveDoctor(selection: DoctorSelection): ResolvedDoctor {
  if (selection.kind === 'listed') {
    return { id: selection.id, name: selection.name };
  }
  if (selection.kind === 'unlisted' && selection.name.trim()) {
    return { id: null, name: selection.name };
  }
  throw new Error('Nama dokter wajib dipilih.');
}

interface DoctorSelectProps {
  /** The chosen hospital's id, or null when none is chosen or it is new. */
  placeId: string | null;
  value: DoctorSelection;
  onChange: (value: DoctorSelection) => void;
  disabled?: boolean;
  id?: string;
}

export const DoctorSelect: React.FC<DoctorSelectProps> = ({
  placeId,
  value,
  onChange,
  disabled = false,
  id = 'doctor'
}) => {
  const [doctors, setDoctors] = useState<DoctorOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let mounted = true;
    setDoctors([]);
    setLoadError(false);
    if (!placeId) {
      return undefined;
    }
    setLoading(true);
    dataService
      .getDoctorsForPlace(placeId)
      .then(data => {
        if (mounted) {
          setDoctors(data);
        }
      })
      .catch(() => {
        if (mounted) {
          setLoadError(true);
        }
      })
      .finally(() => {
        if (mounted) {
          setLoading(false);
        }
      });
    return () => {
      mounted = false;
    };
  }, [placeId]);

  const handleSelect = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const next = event.target.value;
    if (!next) {
      onChange({ kind: 'none' });
      return;
    }
    if (next === UNLISTED_VALUE) {
      onChange({ kind: 'unlisted', name: value.kind === 'unlisted' ? value.name : '' });
      return;
    }
    const match = doctors.find(doctor => doctor.id === next);
    if (match) {
      onChange({ kind: 'listed', id: match.id, name: match.name });
    }
  };

  // A stored doctor who is no longer assigned to this hospital still has to
  // show as the current value, or the select silently reads "Pilih dokter"
  // while the profile says otherwise.
  const listed =
    value.kind === 'listed' && !doctors.some(doctor => doctor.id === value.id)
      ? [...doctors, { id: value.id, name: value.name }]
      : doctors;

  const selectValue =
    value.kind === 'listed' ? value.id : value.kind === 'unlisted' ? UNLISTED_VALUE : '';

  return (
    <div className="field">
      <label htmlFor={id}>Nama Dokter</label>
      <select
        id={id}
        value={selectValue}
        onChange={handleSelect}
        disabled={disabled || loading}
      >
        <option value="">{loading ? 'Memuat...' : 'Pilih dokter'}</option>
        {listed.map(doctor => (
          <option key={doctor.id} value={doctor.id}>
            {doctor.name}
          </option>
        ))}
        <option value={UNLISTED_VALUE}>Dokter belum ada di daftar</option>
      </select>

      {!loading && placeId && !loadError && doctors.length === 0 && value.kind !== 'unlisted' ? (
        <div className="subtitle" style={{ marginTop: 4, fontSize: 12 }}>
          Belum ada dokter terdaftar di tempat kontrol ini. Pilih "Dokter belum
          ada di daftar" lalu tulis namanya.
        </div>
      ) : null}
      {loadError ? (
        <div className="subtitle" style={{ marginTop: 4, fontSize: 12 }}>
          Daftar dokter gagal dimuat. Pilih "Dokter belum ada di daftar" lalu tulis
          namanya.
        </div>
      ) : null}

      {value.kind === 'unlisted' ? (
        <>
          <input
            type="text"
            style={{ marginTop: 8 }}
            value={value.name}
            disabled={disabled}
            onChange={event => onChange({ kind: 'unlisted', name: event.target.value })}
            placeholder="Tulis nama dokter"
            aria-label="Nama dokter yang belum ada di daftar"
          />
          <div className="subtitle" style={{ marginTop: 4, fontSize: 12 }}>
            Admin akan menambahkan dokter ini ke daftar.
          </div>
        </>
      ) : null}
    </div>
  );
};

export default DoctorSelect;
